import { useEffect, useRef, useState } from 'preact/hooks';
import { Button } from '@components/buttons';
import { IcoBook, IcoX } from '@components/icons';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { timeago } from 'shared/dateutil';
import { CategoryValue, toCategoryParam } from './category-filter';
import { FilterMenu, FilterOption } from './filter-menu';
import { FolderFilter } from './state';

type CourseOption = RpxResponse<typeof rpx.mediaManager.getManagedFileCourses>['courses'][number];

type Props = {
  value: string | undefined;
  label: string;
  filters: {
    category: CategoryValue;
    search: string;
    folderId: FolderFilter;
    tagId: string | undefined;
  };
  onRemember(id: string, title: string): void;
  onChange(courseId: string | undefined): void;
};

const SEARCH_THRESHOLD = 8;

export function useCourseTitle(courseId: string | undefined) {
  const [titleByCourseId, setTitleByCourseId] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!courseId || titleByCourseId[courseId]) {
      return;
    }
    let cancelled = false;
    rpx.courses
      .getCourseTitle({ id: courseId })
      .then((title) => {
        if (cancelled) {
          return;
        }
        setTitleByCourseId((prev) => ({ ...prev, [courseId]: title || 'Unavailable course' }));
      })
      .catch(() => {
        if (!cancelled) {
          setTitleByCourseId((prev) => ({ ...prev, [courseId]: 'Unavailable course' }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  return {
    title: courseId ? titleByCourseId[courseId] : undefined,
    remember(id: string, title: string) {
      setTitleByCourseId((prev) => ({ ...prev, [id]: title }));
    },
  };
}

export function CourseFilter({ value, label, filters, onRemember, onChange }: Props) {
  const [courses, setCourses] = useState<CourseOption[] | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | undefined>(undefined);
  const request = useRef(0);

  function load() {
    const id = ++request.current;
    setCourses(undefined);
    setHasError(false);
    const archived = filters.folderId === 'archived';
    rpx.mediaManager
      .getManagedFileCourses({
        archived,
        category: toCategoryParam(filters.category),
        search: filters.search || undefined,
        folderId: archived ? undefined : filters.folderId,
        tagId: filters.tagId,
      })
      .then((result) => {
        if (id === request.current) {
          setCourses(result.courses);
          setLastRefreshedAt(result.lastRefreshedAt);
        }
      })
      .catch(() => {
        if (id === request.current) {
          setHasError(true);
        }
      });
  }

  function choose(courseId: string | undefined, title?: string) {
    if (courseId && title) {
      onRemember(courseId, title);
    }
    onChange(courseId);
  }

  const pinned = !!value && !!courses && !courses.some((course) => course.id === value);
  const listed =
    value && courses && pinned ? [{ id: value, title: label, fileCount: 0 }, ...courses] : courses;

  return (
    <div class="flex w-full min-w-0 items-center gap-1 sm:w-auto">
      <FilterMenu
        id="media-course-filter-trigger"
        icon={IcoBook}
        label={value ? label : 'All courses'}
        active={!!value}
        triggerLabel={value ? `Course: ${label}` : 'All courses'}
        menuLabel="Filter by course"
        searchLabel="Search courses"
        showSearch={(courses?.length ?? 0) > SEARCH_THRESHOLD}
        onOpen={load}
      >
        {({ matches, choose: pick }) => {
          const visible = listed?.filter((course) => matches(course.title));
          return (
            <>
              <div class="max-h-64 overflow-y-auto">
                <FilterOption
                  selected={!value}
                  label="All courses"
                  onClick={() => pick(() => choose(undefined))}
                />
                {visible?.map((course) => (
                  <FilterOption
                    key={course.id}
                    selected={course.id === value}
                    label={course.title}
                    count={course.fileCount}
                    onClick={() => pick(() => choose(course.id, course.title))}
                  />
                ))}
                {!courses && !hasError && (
                  <p class="p-2 text-sm text-gray-500" role="status">
                    Loading courses…
                  </p>
                )}
                {hasError && (
                  <div
                    class="flex items-center justify-between gap-2 p-2 text-sm text-red-700"
                    role="alert"
                  >
                    <span>Courses could not load.</span>
                    <button
                      type="button"
                      class="min-h-11 rounded-full px-3 font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      onClick={(e) => {
                        e.currentTarget
                          .closest('[role="dialog"]')
                          ?.querySelector<HTMLElement>('button')
                          ?.focus();
                        load();
                      }}
                    >
                      Try again
                    </button>
                  </div>
                )}
                {listed && listed.length === 0 && (
                  <p class="p-2 text-sm text-gray-500">None of these files are used in a course.</p>
                )}
                {listed && listed.length > 0 && visible?.length === 0 && (
                  <p class="p-2 text-sm text-gray-500">No courses match your search</p>
                )}
              </div>
              {courses && ((courses.length > 0 && !pinned) || lastRefreshedAt) && (
                <div class="mt-1 flex flex-col gap-1 border-t border-gray-200 px-2 pt-2 pb-1 text-xs text-gray-500">
                  {courses.length > 0 && !pinned && (
                    <p>Only courses that use at least one of these files are listed.</p>
                  )}
                  {lastRefreshedAt && <p>Course use updated {timeago(lastRefreshedAt, ' ago')}</p>}
                </div>
              )}
            </>
          );
        }}
      </FilterMenu>
      {value && (
        <Button
          class="flex size-10.5 shrink-0 items-center justify-center rounded-sm text-gray-500 hover:bg-indigo-50 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          onClick={() => onChange(undefined)}
          aria-label="Clear course filter"
        >
          <IcoX class="size-4" />
        </Button>
      )}
    </div>
  );
}
